import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  signal,
  viewChild,
  WritableSignal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Button } from 'primeng/button';
import { MenuModule } from 'primeng/menu';
import { MenubarModule } from 'primeng/menubar';
import { WidgetComponent } from '../widget/widget.component';
import {
  CdkDragEnd,
  CdkDragMove,
  DragDropModule,
} from '@angular/cdk/drag-drop';
import { wrapGrid } from 'animate-css-grid';
import { Chart } from 'chart.js';
import { Dashboard, Widget } from '../models/dashboard';
import { DialogService } from 'primeng/dynamicdialog';
import { DashboardService } from '../services/dashboard.service';
import { ResizableModule, ResizeEvent } from 'angular-resizable-element';
import { DashboardFactory } from '../factory/dashboard.factory';
import { CreateOrUpdateDashboardDialogComponent } from '../create-or-update-dashboard-dialog/create-or-update-dashboard-dialog.component';

@Component({
  selector: 'app-create-dashboard',
  providers: [HttpClient],
  imports: [
    WidgetComponent,
    Button,
    MenuModule,
    MenubarModule,
    DragDropModule,
    FormsModule,
    CommonModule,
    ResizableModule,
  ],
  standalone: true,
  templateUrl: './create-dashboard.component.html',
  styleUrl: './create-dashboard.component.css',
})
export class CreateDashboardComponent implements AfterViewInit {
  // Injected Services
  dialogService = inject(DialogService);
  dashboardService = inject(DashboardService);

  // Referenz auf das Dashboard-Element im Template
  dashboardElementRef = viewChild.required<ElementRef>('dashboard');

  // Signal für das Dashboard-Objekt
  dashboard: WritableSignal<Dashboard> = signal(DashboardFactory.createEmpty());

  // Vorschau für das Drag-and-Drop-Widget
  dragPreview: Partial<Widget & { visible: boolean }> = {
    visible: false,
    columnPosition: 1,
    rowPosition: 1,
  };

  // Konstanten für das Grid-Layout
  private readonly columnsCount = 4; // Anzahl der Spalten
  private readonly baseRowHeight = 150; // Basis-Höhe einer Zeile

  // Öffentliche Variablen für Grid-Berechnungen
  public colWidth!: number; // Spaltenbreite
  public rowHeight!: number; // Zeilenhöhe
  public gridGap!: number; // Abstand zwischen den Grid-Elementen

  // Funktion zum Entfernen von Animationen
  clearAnimations = () => {};

  constructor(private cdr: ChangeDetectorRef) {}

  // Initialisierung des Dashboards
  ngOnInit() {
    this.dashboardService.reset(); // Widgets zurücksetzen
    const { unwrapGrid } = wrapGrid(this.dashboardElementRef().nativeElement, {
      duration: 300,
    });
    this.clearAnimations = unwrapGrid;

    // Standardfarbe für Charts setzen
    Chart.defaults.color = 'white';
  }

  // Nach der Initialisierung der View: Grid-Größen berechnen
  ngAfterViewInit(): void {
    this.calculateGridSizes();
  }

  // Ressourcen freigeben
  ngOnDestroy() {
    this.clearAnimations();
  }

  // Dashboard speichern
  saveDashboard() {
    this.dashboard().widgets = this.dashboardService.addedWidgets(); // Widgets hinzufügen
    this.dialogService.open(CreateOrUpdateDashboardDialogComponent, {
      header: 'Dashboard erstellen',
      width: '60%',
      modal: true,
      data: {
        dashboard: this.dashboard,
        isNew: true,
      },
    });
  }

  // Event: Drag-and-Drop gestartet
  onDragStarted() {
    this.clearAnimations(); // Animationen deaktivieren
  }

  // Event: Drag-and-Drop bewegt
  onDragMoved(event: CdkDragMove, widget: Widget) {
    const { newCol, newRow } = this.dashboardService.calculateGridPosition(
      event,
      widget,
      this.dashboardElementRef,
      this.gridGap,
      this.columnsCount,
      this.rowHeight
    );

    // Vorschau aktualisieren
    Object.assign(this.dragPreview, {
      visible: true,
      columnPosition: newCol,
      rowPosition: newRow,
      columnSpan: widget.columnSpan,
      rowSpan: widget.rowSpan,
    });
  }

  // Event: Drag-and-Drop beendet
  onDragEnded(event: CdkDragEnd, widget: Widget) {
    const { unwrapGrid } = wrapGrid(this.dashboardElementRef().nativeElement, {
      duration: 300,
    });
    this.clearAnimations = unwrapGrid;

    const { newCol, newRow } = this.dashboardService.calculateGridPosition(
      event,
      widget,
      this.dashboardElementRef,
      this.gridGap,
      this.columnsCount,
      this.rowHeight
    );

    // Vorschau ausblenden und Widget-Position aktualisieren
    this.dragPreview.visible = false;
    widget.columnPosition = newCol;
    widget.rowPosition = newRow;

    event.source.reset(); // Drag-Quelle zurücksetzen
    this.dashboardService.resolveCollisions(widget); // Kollisionen auflösen
    this.dashboardService.updateWidget(widget.widgetId, widget); // Widget aktualisieren
  }

  // Event: Resize beendet
  onResizeEnd(event: ResizeEvent, widget: Widget) {
    const gridRect =
      this.dashboardElementRef().nativeElement.getBoundingClientRect();
    const colWidth = gridRect.width / 4; // Spaltenbreite berechnen
    const newColSpan = Math.max(
      1,
      Math.round((event.rectangle.width ?? 0) / colWidth)
    );
    const newRowSpan = Math.max(
      1,
      Math.round((event.rectangle.height ?? 0) / 150)
    );

    // Widget-Größe aktualisieren
    widget.columnSpan = newColSpan;
    widget.rowSpan = newRowSpan;
  }

  // Berechnung der Grid-Größen
  private calculateGridSizes() {
    const gridEl = this.dashboardElementRef().nativeElement;
    const style = getComputedStyle(gridEl);
    const rect = gridEl.getBoundingClientRect();

    // Padding und Gaps auslesen
    const padL = parseFloat(style.paddingLeft) || 0;
    const padR = parseFloat(style.paddingRight) || 0;
    this.gridGap = parseFloat(style.columnGap) || parseFloat(style.gap) || 0;
    const totalW = rect.width - padL - padR;
    const totalGap = this.gridGap * (this.columnsCount - 1);

    // Spaltenbreite und Zeilenhöhe berechnen
    this.colWidth = (totalW - totalGap) / this.columnsCount;
    this.rowHeight = this.baseRowHeight + this.gridGap;

    // ChangeDetector auslösen, um die neuen Werte zu rendern
    this.cdr.detectChanges();
  }
}
